import dayjs from 'dayjs'
import type { Event } from '@/types/resources/event'

// Backend creates a placeholder event (to satisfy the Campaign table's NOT NULL
// constraint) when a data capture has no linked event. Its name is prefixed with
// this sentinel. Use the helpers below rather than string-matching inline.
export const AUTO_CREATED_EVENT_PREFIX = 'auto-created::'

export const isAutoCreatedEvent = (event?: { name?: string | null } | null): boolean =>
  event?.name?.startsWith(AUTO_CREATED_EVENT_PREFIX) ?? false

// Name to display for a campaign's linked event, blank when there is no real
// (user-linked) event so callers can show their own empty state.
export const linkedEventName = (event?: { name?: string | null } | null): string =>
  isAutoCreatedEvent(event) ? '' : (event?.name ?? '')

export const eventLastImport = ({ meta, lastImport }: any) => {
  // Events store the last import time in the Event meta data, in each synch.
  if (!meta?.synch) {
    return
  }

  const { synch } = meta

  // For each synch value accumulate the sys-mtime (or modified-time) and track the most
  // recent import.
  let mostRecentModifiedTime: any = null
  if (lastImport) {
    return dayjs(lastImport)
  }
  else if (synch) {
    Object.keys(synch).forEach((key) => {
      // TODO: some latest synch objects  has no modified and lastSyncTimeEnd fields, need to fix this after check backend.
      const sysMTime = synch[key].sysMtime
        || synch[key].modified
        || synch[key].lastSynchTimeEnd
      if (!mostRecentModifiedTime || dayjs(sysMTime).isAfter(mostRecentModifiedTime))
        mostRecentModifiedTime = dayjs.utc(sysMTime)
    })
  }

  return mostRecentModifiedTime
}

export const isSyncing = (event: any) => {
  if (event.meta && event.meta.synch) {
    const synchKeys = Object.keys(event.meta.synch)

    return synchKeys.some((key) => {
      // NOTE/RK
      // event.meta.synch values without state or integrationOid will
      // have undefined, not null (like events with CSV imports)
      // Need to account for this when checking sync status
      const localState = event.meta.synch[key].state || null
      const integrationOid = event.meta.synch[key].integrationOid || null
      return (localState === 'pending' || localState === 'in-progress') && integrationOid !== null
    })
  }
  else {
    return false
  }
}

export const isMerging = (event: any) => {
  if (event.meta && event.meta.merge) {
    return event.meta.merge.state === 'in-progress'
  }
  else {
    return false
  }
}

export function generateDateForEmail(event: Event): string {
  let format
  const timeZoneAbbr = dayjs().tz(event.timeZone).zoneAbbr()
  if (/^[+-]?\d+$/.test(timeZoneAbbr)) {
    // If timezone abbreviation is -12 ~ +12, let's add prefix "GMT" to it, otherwise it's meaningless.
    format = 'dddd MMMM Do YYYY, hh:mma [GMT]z'
  }
  else {
    format = 'dddd MMMM Do YYYY, hh:mma z'
  }
  return dayjs(event.startDate).tz(event.timeZone).format(format)
};

export const getEventPurchaseTicketUrl = (event: Event): string | null => {
  return event.additionalInfo?.purchaseTicketUrl || null
}

export function generateDateRangeForEventsModal(event: Event): string | null {
  const {
    startDate,
    endDate,
    timeZone,
  } = event

  if (!timeZone) {
    return null
  }

  if (!startDate && !endDate) {
    return null
  }
  else if (startDate && !endDate) {
    return dayjs.utc(startDate).tz(timeZone).format('MMM D, YYYY')
  }
  else {
    const startDayJS = dayjs.utc(startDate).tz(timeZone)
    const endDay = dayjs.utc(endDate).tz(timeZone)
    if (startDayJS.diff(endDay, 'days') === 0) {
      return startDayJS.format('MMM D, YYYY')
    }
    else if (startDayJS.diff(endDay, 'years') === 0) {
      return `${startDayJS.format('MMM D')} - ${endDay.format('MMM D, YYYY')}`
    }
    else {
      return `${startDayJS.format('MMM D, YYYY')} - ${endDay.format('MMM D, YYYY')}`
    }
  }
};
